import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";

const SUGGESTIONS = [
  "Où est mon colis ?",
  "Quels sont les délais en maritime ?",
  "Pourquoi mon colis est en dédouanement ?",
];

const sessionId = (() => {
  const key = "htl_chat_session";
  let id = window.localStorage.getItem(key);
  if (!id) {
    id = `web-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    window.localStorage.setItem(key, id);
  }
  return id;
})();

export const ChatWidget = () => {
  const { trackingNumber } = useParams();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const ask = async (question) => {
    const text = question.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          tracking_number: trackingNumber || null,
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const parsed = JSON.parse(payload);
          const piece = parsed.delta || parsed.error || "";
          setMessages((m) => {
            const next = [...m];
            next[next.length - 1] = {
              role: "assistant",
              content: next[next.length - 1].content + piece,
            };
            return next;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: "Assistant momentanément indisponible. Réessayez dans un instant.",
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-4 text-sm font-bold text-white shadow-2xl transition-transform hover:scale-105"
          data-testid="chat-open-button"
        >
          <MessageSquare className="h-5 w-5" />
          <span className="hidden sm:inline">Besoin d'aide ?</span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-0 right-0 z-50 flex h-[85vh] w-full flex-col overflow-hidden border border-slate-200 bg-white shadow-2xl sm:bottom-5 sm:right-5 sm:h-[560px] sm:w-[400px] sm:rounded-2xl"
          data-testid="chat-panel"
        >
          <div className="flex items-center justify-between bg-[#1E3A8A] px-4 py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-blue-200" />
              <div>
                <p className="text-sm font-bold text-white">Assistant Haba Trans</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-blue-200">
                  {trackingNumber ? `Colis ${trackingNumber}` : "Suivi & informations"}
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-blue-100 hover:text-white"
                    data-testid="chat-close-button">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto bg-[#F8FAFC] p-4" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm text-slate-700">
                  Bonjour ! Je peux vous renseigner sur votre colis, les délais et le dédouanement.
                  {!trackingNumber && " Indiquez votre numéro de suivi (HTL-2026-XXXXX)."}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => ask(s)}
                            className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-[#1E3A8A] transition-colors hover:bg-blue-100"
                            data-testid={`chat-suggestion-${s.slice(0, 10)}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                   data-testid={`chat-message-${m.role}-${i}`}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm ${
                    m.role === "user"
                      ? "bg-[#2563EB] text-white"
                      : "border border-slate-200 bg-white text-slate-800"
                  }`}
                >
                  {m.content || (streaming ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              ask(input);
            }}
            className="flex gap-2 border-t border-slate-200 bg-white p-3"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Posez votre question…"
              className="h-11"
              data-testid="chat-input"
            />
            <Button type="submit" disabled={streaming}
                    className="h-11 bg-[#2563EB] px-4 text-white hover:bg-[#1D4ED8]"
                    data-testid="chat-send-button">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};
